import { ChangeEvent, FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Link, useNavigate, useParams } from "react-router-dom";

import { zodResolver } from "@hookform/resolvers/zod";

import { Controller, useForm } from "react-hook-form";

import { enUS, fr, de } from "date-fns/locale";
import {
	faChevronDown,
	faChevronUp,
	faList,
	faMegaphone,
} from "@fortawesome/pro-regular-svg-icons";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enUS as enUSPicker } from "@mui/x-date-pickers/locales";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import Datepicker, { DateValueType } from "react-tailwindcss-datepicker";

import { Button } from "@/components/Button/Button";
import { useGetListQuery, useUpdateListMutation } from "@/pages/Private/redux/list/list.api";

import { Icon, LoadingOverlay } from "@/components";

import { ButtonColor } from "@/components/Button/types";

import {
	GetContactInformation,
	GetEmailInformation,
	GetPhoneInformation,
	JobChangeIntervalUnit,
	ListSourceType,
	ListRecurrence,
	ListStatus,
	ListType,
	SearchBase,
	SmartListFrequency,
	UpdateType,
} from "@/enum/list.enum";
import { ERROR_TYPE, getAllErrors, renderErrorMessages, useSearch } from "@/utils";

import { Checkbox } from "@/components/Checkbox/Checkbox";
import { InputField } from "@/components/InputField/InputField";

import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";

import { useGetLemlistsQuery } from "@/pages/Private/redux/lemlist/lemlist.api";

import { AutoComplete, AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";

import { Toggle } from "@/components/Toggle/Toggle";

import { Information } from "@/components/Information/Information";

import { useGetWebhooksQuery } from "@/pages/Private/redux/webhook/webhook.api";

import { classNames } from "@/utils/classNames";

import { useSnackBar } from "@/utils/useSnackbar";

import { WebhookType } from "@/enum/webhook.enum";

import { Tooltip } from "@/components/Tooltip/Tooltip";

import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { CampaignStatus } from "@/enum/campaign-status.enum";

import { ListBuilderLayout } from "../../components/ListBuilderLayout";

import {
	List,
	ListWebhook,
	SaveListSettings,
	SaveListSettingsValidationSchema,
	SignalIntervalMode,
	UpdateList,
} from "../../schema/list";
import { getAvailableSteps, isDetailsNeeded, getDoneSteps } from "../../utils/create-list-steps";
import { reportActivationFailure } from "../../utils/listRefusalMessage";
import { resolveInitialListName, shouldSuggestListActivation } from "./utils/listSettings.utils";
import { DEFAULT_JOB_CHANGE_INTERVALS, DEFAULT_SIGNAL_INTERVALS } from "./constants";
import { Stepper } from "../ListEditDetails/components/Stepper";

import { AutoAssignRoutingLine } from "../../components/CampaignRouting/AutoAssignRoutingLine";
import { FeaturesCodes } from "../../../Users/enum/codes";

import {
	ActivateListModal,
	LeadNoveltySetting,
	ListEditSettingCrmSections,
	ListEditSettingEmailDeliverability,
	ListJobChangeSettingsSection,
} from "./components";
import { ListRunScheduleSection } from "./components/ListRunScheduleSection";
import {
	useListSettingsData,
	useHubspotSyncTrigger,
	useHubspotLeadSyncTrigger,
	useHubspotDealCreationTrigger,
	useListEmailDeliverabilityOverrides,
	useZohoSyncTrigger,
	useDynamicsSyncTrigger,
	useSalesforceSyncTrigger,
	usePipedriveSyncTrigger,
} from "./hooks";

export const ListEditSetting: FunctionComponent = () => {
	const userLocale = navigator.language || "en-US";

	const localeMap = {
		"en-US": enUS,
		"en-GB": enUS, // you can alias if you don’t mind slight differences
		"fr-FR": fr,
		"de-DE": de,
		de: de,
		en: enUS,
		fr: fr,
	};

	const currentLocale = localeMap?.[userLocale as keyof typeof localeMap] || enUS;

	const { t } = useTranslation();
	const { id = 0 } = useParams();
	const navigate = useNavigate();

	const ts = (key: string) => t(`list.${key}`);
	const tsSettings = (key: string) => t(`list.settings.${key}`);

	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const isSequencerActive = useIsFeatureActive(FeaturesCodes.SEQUENCER);
	const isSmartListActive = useIsFeatureActive(FeaturesCodes.SMART_LIST);

	// Use extracted HubSpot sync trigger hook
	const {
		isHubspotEnabled,
		isConnected: isHubspotConnected,
		listSyncTrigger,
		selectedHubspotSyncTrigger,
		setSelectedHubspotSyncTrigger,
		saveSyncTrigger,
	} = useHubspotSyncTrigger({ listId: +id });
	const {
		listLeadSyncTrigger,
		selectedLeadSyncTrigger,
		setSelectedLeadSyncTrigger,
		saveLeadSyncTrigger,
	} = useHubspotLeadSyncTrigger({ listId: +id });

	// Use extracted Zoho sync trigger hook
	const {
		isZohoEnabled,
		isConnected: isZohoConnected,
		listSyncTrigger: zohoListSyncTrigger,
		selectedZohoSyncTrigger,
		setSelectedZohoSyncTrigger,
		saveSyncTrigger: saveZohoSyncTrigger,
	} = useZohoSyncTrigger({ listId: +id });

	// Use extracted Pipedrive sync trigger hook
	const {
		isPipedriveEnabled,
		isConnected: isPipedriveConnected,
		listSyncTrigger: pipedriveListSyncTrigger,
		selectedPipedriveSyncTrigger,
		setSelectedPipedriveSyncTrigger,
		saveSyncTrigger: savePipedriveSyncTrigger,
	} = usePipedriveSyncTrigger({ listId: +id });

	// Dynamics 365 per-list record-sync trigger (there is no per-list opportunity trigger).
	const {
		isDynamicsEnabled,
		isConnected: isDynamicsConnected,
		listSyncTrigger: dynamicsListSyncTrigger,
		selectedDynamicsSyncTrigger,
		setSelectedDynamicsSyncTrigger,
		saveSyncTrigger: saveDynamicsSyncTrigger,
	} = useDynamicsSyncTrigger({ listId: +id });

	// Salesforce per-list record-sync trigger (there is no per-list opportunity trigger).
	const {
		isSalesforceEnabled,
		isConnected: isSalesforceConnected,
		listSyncTrigger: salesforceListSyncTrigger,
		selectedSalesforceSyncTrigger,
		setSelectedSalesforceSyncTrigger,
		saveSyncTrigger: saveSalesforceSyncTrigger,
	} = useSalesforceSyncTrigger({ listId: +id });

	const [activateCampaign, setActivateCampaign] = useState<boolean>(false);

	const [advancedSettings, setAdvancedSettings] = useState<boolean>(false);
	const [signalSettings, setSignalSettings] = useState<boolean>(true);
	const [allowEndDate, setAllowEndDate] = useState<boolean>(false);
	const [allowMaxProspects, setAllowMaxProspects] = useState<boolean>(true);
	const [allowFindEmail, setAllowFindEmail] = useState<boolean>(false);
	const [allowFindPhone, setAllowFindPhone] = useState<boolean>(false);
	const [allowOnlyContactInformation, setAllowOnlyContactInformation] = useState<boolean>(false);

	// Use extracted data fetching hook
	const {
		data,
		isFetching,
		lemlists,
		isLoadingLemlists,
		setSearchLemlist,
		webhooks,
		blockListFrequency,
		blockListFrequencyInfo,
	} = useListSettingsData(+id);

	// Use extracted HubSpot deal creation trigger hook
	const {
		selectedDealCreationTrigger,
		setSelectedDealCreationTrigger,
		effectiveTrigger: dealCreationEffectiveTrigger,
		hasDealCreationTriggerChanged,
	} = useHubspotDealCreationTrigger({ listId: +id, listData: data });

	// Per-list email deliverability overrides (PREC-4261): only changed fields are saved.
	const { overrides, hasEmailDeliverabilityChanged, emailDeliverabilityUpdate } =
		useListEmailDeliverabilityOverrides(data);

	const [selectedCampaign, setSelectedCampaign] = useState<AutoCompleteItem | null>();

	const [searchBase, setSearchBase] = useState<SearchBase | null>(data?.searchBase || null);

	const [showActivateListModal, setShowActivateListModal] = useState<boolean>(false);

	const [update, { isLoading }] = useUpdateListMutation();

	const {
		handleSubmit,
		setValue,
		control,
		formState: { errors, isDirty },
		reset,
		watch,
	} = useForm<SaveListSettings>({
		defaultValues: {
			name: data?.name,
			searchBase: data?.searchBase,
			sourceType: data?.sourceType,
			maxProspects:
				data?.maxProspects?.toString() && data?.maxProspects?.toString() !== ""
					? data?.maxProspects?.toString()
					: "",
			maxPerCompany:
				data?.apollos?.[0]?.config?.maxPerCompany?.toString() &&
				data?.apollos?.[0]?.config?.maxPerCompany?.toString() !== ""
					? data?.apollos?.[0]?.config?.maxPerCompany?.toString()
					: [ListSourceType.CSV_ENRICHMENTS, ListSourceType.CUSTOM_EVENTS].includes(
								data?.sourceType as ListSourceType
						  )
						? ""
						: "3",
			type: data?.type,
			recurrence: data?.recurrence ?? ListRecurrence.DAILY,
			campaigns: [],
			onlyNewLeads: data?.onlyNewLeads || false,
			findPersonContactMode: data?.findPersonContactMode,
			getContactInformation:
				(data?.getContactInformation as GetContactInformation) || GetContactInformation.NONE,
			getEmailInformation:
				(data?.getEmailInformation as GetEmailInformation) || GetEmailInformation.NONE,
			getPhoneInformation:
				(data?.getPhoneInformation as GetPhoneInformation) || GetPhoneInformation.NONE,
			maxDate: data?.maxDate || "",
			webhooks: data?.webhooks.map((webhook) => webhook.ListWebhook) || [],
			priority: data?.priority,
			// Job Change tracking fields
			jobChangeIntervalPrio1: data?.jobChangeIntervalPrio1 ?? DEFAULT_JOB_CHANGE_INTERVALS.prio1,
			jobChangeIntervalPrio1Unit:
				data?.jobChangeIntervalPrio1Unit ?? DEFAULT_JOB_CHANGE_INTERVALS.prio1Unit,
			jobChangeIntervalPrio2: data?.jobChangeIntervalPrio2 ?? DEFAULT_JOB_CHANGE_INTERVALS.prio2,
			jobChangeIntervalPrio2Unit:
				data?.jobChangeIntervalPrio2Unit ?? DEFAULT_JOB_CHANGE_INTERVALS.prio2Unit,
			jobChangeIntervalPrio3: data?.jobChangeIntervalPrio3 ?? DEFAULT_JOB_CHANGE_INTERVALS.prio3,
			jobChangeIntervalPrio3Unit:
				data?.jobChangeIntervalPrio3Unit ?? DEFAULT_JOB_CHANGE_INTERVALS.prio3Unit,
			// Job Change deactivation settings
			jobChangeDeactivateOnChangedEmployer: data?.jobChangeDeactivateOnChangedEmployer ?? true,
			jobChangeDeactivateOnChangedRole: data?.jobChangeDeactivateOnChangedRole ?? false,
			jobChangeDeactivateOnAddedEmployer: data?.jobChangeDeactivateOnAddedEmployer ?? false,
			jobChangeDeactivateOnAddedRole: data?.jobChangeDeactivateOnAddedRole ?? false,
			// Signal interval fields (for TRACKER lists)
			signalIntervalPrio1: data?.signalIntervalPrio1 ?? DEFAULT_SIGNAL_INTERVALS.prio1,
			signalIntervalPrio1Unit: data?.signalIntervalPrio1Unit ?? DEFAULT_SIGNAL_INTERVALS.prio1Unit,
			signalIntervalPrio2: data?.signalIntervalPrio2 ?? DEFAULT_SIGNAL_INTERVALS.prio2,
			signalIntervalPrio2Unit: data?.signalIntervalPrio2Unit ?? DEFAULT_SIGNAL_INTERVALS.prio2Unit,
			signalIntervalPrio3: data?.signalIntervalPrio3 ?? DEFAULT_SIGNAL_INTERVALS.prio3,
			signalIntervalPrio3Unit: data?.signalIntervalPrio3Unit ?? DEFAULT_SIGNAL_INTERVALS.prio3Unit,
			// Signal interval mode configuration (for TRACKER lists)
			signalIntervalMode: data?.signalIntervalMode ?? SignalIntervalMode.ROLLING,
			signalScheduleStartDate: data?.signalScheduleStartDate ?? null,
			// Auto-assign to campaign after lead scoring
			autoAssignToCampaign: data?.autoAssignToCampaign ?? false,
			autoAssignMinScore: data?.autoAssignMinScore ?? null,
			// Smart List fields
			smartList: data?.smartList
				? {
						frequency: data.smartList.frequency ?? SmartListFrequency.DAILY,
						filter: data.smartList.filter ?? null,
						semanticSearchProspects: data.smartList.semanticSearchProspects ?? null,
					}
				: null,
		},
		resolver: zodResolver(SaveListSettingsValidationSchema),
	});

	const formValues = watch();

	const isSmartList = data?.sourceType === ListSourceType.SMART_LIST;

	useEffect(() => {
		if (isSmartList && isSmartListActive === false) {
			navigate(`/app/lists/detail/${id}`);
		}
	}, [isSmartList, isSmartListActive, navigate, id]);

	const frequencyOptions: AutoCompleteItem[] = useMemo(
		() =>
			Object.values(SmartListFrequency).map((freq) => ({
				id: freq,
				title: tsSettings(freq),
			})),
		[tsSettings]
	);

	const intervalNumberOptions: DropdownAutoCompleteItem[] = useMemo(
		() =>
			[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((num) => ({
				id: num,
				title: num.toString(),
			})),
		[]
	);

	const intervalUnitOptions: DropdownAutoCompleteItem[] = useMemo(
		() =>
			Object.values(JobChangeIntervalUnit).map((unit) => ({
				id: unit,
				title: tsSettings(`jobChangeIntervalUnits.${unit}`),
			})),
		[tsSettings]
	);

	const findNumberOption = (id: number | null | undefined, fallback: number) =>
		intervalNumberOptions.find((o) => o.id === (id ?? fallback)) ??
		intervalNumberOptions[fallback - 1];

	const findUnitOption = (
		id: JobChangeIntervalUnit | null | undefined,
		fallback: JobChangeIntervalUnit
	) => intervalUnitOptions.find((o) => o.id === (id ?? fallback)) ?? intervalUnitOptions[0];

	useEffect(() => {
		if (searchBase) {
			setValue("searchBase", searchBase, {
				shouldDirty: false,
				shouldValidate: true,
			});
		}
	}, [searchBase, setValue]);

	useEffect(() => {
		if (data) {
			if (data.campaignConfig) {
				setActivateCampaign(true);
			} else {
				setActivateCampaign(false);
			}

			if (data.getContactInformation !== GetContactInformation.NONE) {
				setAllowOnlyContactInformation(true);
			} else {
				setAllowOnlyContactInformation(false);
			}

			if (data.getEmailInformation !== GetEmailInformation.NONE) {
				setAllowFindEmail(true);
			} else {
				setAllowFindEmail(false);
			}

			if (data.getPhoneInformation !== GetPhoneInformation.NONE) {
				setAllowFindPhone(true);
			} else {
				setAllowFindPhone(false);
			}

			if (data.maxDate) {
				setAllowEndDate(true);
			} else {
				setAllowEndDate(false);
			}

			if (data.maxProspects) {
				setAllowMaxProspects(true);
			} else {
				setAllowMaxProspects(false);
			}

			setValue("name", data.name, {
				shouldDirty: false,
				shouldValidate: true,
			});

			const currentFormState = {
				name: data?.name,
				searchBase: data?.searchBase,
				sourceType: data?.sourceType,
				maxProspects:
					data?.maxProspects?.toString() && data?.maxProspects?.toString() !== ""
						? data?.maxProspects?.toString()
						: "",
				maxPerCompany:
					data?.apollos?.[0]?.config?.maxPerCompany?.toString() &&
					data?.apollos?.[0]?.config?.maxPerCompany?.toString() !== ""
						? data?.apollos?.[0]?.config?.maxPerCompany?.toString()
						: [ListSourceType.CSV_ENRICHMENTS, ListSourceType.CUSTOM_EVENTS].includes(
									data?.sourceType as ListSourceType
							  )
							? ""
							: "3",
				type: data?.type,
				recurrence: data?.recurrence ?? ListRecurrence.DAILY,
				campaigns: [],
				onlyNewLeads: data?.onlyNewLeads || false,
				findPersonContactMode: data?.findPersonContactMode,
				getContactInformation:
					(data?.getContactInformation as GetContactInformation) || GetContactInformation.NONE,
				getEmailInformation:
					(data?.getEmailInformation as GetEmailInformation) ||
					GetEmailInformation.WHEN_TRIED_TO_SEND_EMAIL,
				getPhoneInformation:
					(data?.getPhoneInformation as GetPhoneInformation) || GetPhoneInformation.NONE,
				maxDate: data?.maxDate || "",
				webhooks: (data?.webhooks?.map((webhook) => webhook.ListWebhook) as ListWebhook[]) || [],
				priority: data?.priority,
				// Job Change tracking fields
				jobChangeIntervalPrio1: data?.jobChangeIntervalPrio1 ?? 3,
				jobChangeIntervalPrio1Unit:
					data?.jobChangeIntervalPrio1Unit ?? JobChangeIntervalUnit.MONTHS,
				jobChangeIntervalPrio2: data?.jobChangeIntervalPrio2 ?? 3,
				jobChangeIntervalPrio2Unit:
					data?.jobChangeIntervalPrio2Unit ?? JobChangeIntervalUnit.MONTHS,
				jobChangeIntervalPrio3: data?.jobChangeIntervalPrio3 ?? 3,
				jobChangeIntervalPrio3Unit:
					data?.jobChangeIntervalPrio3Unit ?? JobChangeIntervalUnit.MONTHS,
				// Job Change deactivation settings
				jobChangeDeactivateOnChangedEmployer: data?.jobChangeDeactivateOnChangedEmployer ?? true,
				jobChangeDeactivateOnChangedRole: data?.jobChangeDeactivateOnChangedRole ?? false,
				jobChangeDeactivateOnAddedEmployer: data?.jobChangeDeactivateOnAddedEmployer ?? false,
				jobChangeDeactivateOnAddedRole: data?.jobChangeDeactivateOnAddedRole ?? false,
				// Signal interval fields (for TRACKER lists)
				signalIntervalPrio1: data?.signalIntervalPrio1 ?? 2,
				signalIntervalPrio1Unit: data?.signalIntervalPrio1Unit ?? JobChangeIntervalUnit.WEEKS,
				signalIntervalPrio2: data?.signalIntervalPrio2 ?? 1,
				signalIntervalPrio2Unit: data?.signalIntervalPrio2Unit ?? JobChangeIntervalUnit.MONTHS,
				signalIntervalPrio3: data?.signalIntervalPrio3 ?? 2,
				signalIntervalPrio3Unit: data?.signalIntervalPrio3Unit ?? JobChangeIntervalUnit.MONTHS,
				// Signal interval mode configuration (for TRACKER lists)
				signalIntervalMode: data?.signalIntervalMode ?? SignalIntervalMode.ROLLING,
				signalScheduleStartDate: data?.signalScheduleStartDate ?? null,
				// Auto-assign to campaign after lead scoring
				autoAssignToCampaign: data?.autoAssignToCampaign ?? false,
				autoAssignMinScore: data?.autoAssignMinScore ?? null,
				// Smart List fields
				smartList: data?.smartList
					? {
							frequency: data.smartList.frequency ?? SmartListFrequency.DAILY,
							filter: data.smartList.filter ?? null,
							semanticSearchProspects: data.smartList.semanticSearchProspects ?? null,
						}
					: null,
			};

			reset(currentFormState);

			// First settings visit of a freshly created list (name still the auto-generated default):
			// pre-fill the name with the first added source's name so the user gets a meaningful
			// default. Marked dirty so it persists on save; once saved, the backend clears
			// `nameAutoGenerated` and the resolved name matches `data.name`, so this no longer fires.
			const initialName = resolveInitialListName(data);

			if (initialName !== data.name) {
				setValue("name", initialName, {
					shouldDirty: true,
					shouldValidate: true,
				});
			}

			if (
				["GET_MANAGING_DIRECTOR", "GET_INFO_AT", "GET_PERSON_FROM_OFFER"].includes(
					data?.scrapeLevel ?? ""
				)
			) {
				setValue("searchBase", SearchBase.SOURCE, {
					shouldDirty: false,
					shouldValidate: true,
				});

				setSearchBase(SearchBase.SOURCE);
			} else if (["GET_ENRICHMENT"].includes(data?.scrapeLevel ?? "")) {
				setValue("searchBase", SearchBase.COMPANY, {
					shouldDirty: false,
					shouldValidate: true,
				});

				setSearchBase(SearchBase.COMPANY);
			} else {
				setValue("searchBase", SearchBase.PERSON, {
					shouldDirty: false,
					shouldValidate: true,
				});

				setSearchBase(SearchBase.PERSON);
			}

			if (data.lemlists?.length) {
				setSelectedCampaign({
					id: data.lemlists[0].id,
					title: data.lemlists[0].name,
				});
			}
		}
	}, [data]);

	const onSubmit = async (values: SaveListSettings) => {
		try {
			if (data) {
				await updateCurrentList(data, values);

				// Update HubSpot sync trigger if changed
				await saveSyncTrigger();
				await saveLeadSyncTrigger();

				// Update Zoho sync trigger if changed
				await saveZohoSyncTrigger();

				// Update Pipedrive sync trigger if changed
				await savePipedriveSyncTrigger();
				await saveDynamicsSyncTrigger();
				await saveSalesforceSyncTrigger();

				reset(values);
			}
		} catch (err) {
			console.error(err);
		}
	};

	const handleActivateList = async (id: number, activateCampaign: boolean) => {
		if (isLoading) {
			return;
		}

		const updater: Partial<UpdateList> = {
			id: id,
			updateType: UpdateType.STATUS_WITH_REFETCH,
			status: ListStatus.ACTIVE,
			campaignStatus: activateCampaign ? CampaignStatus.RUNNING : CampaignStatus.DRAFT, // arch-allow list-activation: the Activate-list modal's own "Activate outreach for this list" toggle
		};

		try {
			await update({ ...updater }).unwrap();

			setShowActivateListModal(false);
			navigate("/app/lists");
		} catch (error) {
			reportActivationFailure(error, ts("status.stoppingError"), handleOpenSnackbar);
		}
	};

	const updateCurrentList = async (data: List, values: SaveListSettings) => {
		try {
			let updater: Partial<UpdateList> = {
				id: data.id,
				updateType: UpdateType.SETTINGS,
			};

			if (data) {
				updater = {
					...updater,
					...{
						id: data.id,
						name: values.name,
						maxProspects: values.maxProspects ? +values.maxProspects : null,
						maxPerCompany: values.maxPerCompany ? +values.maxPerCompany : null,
						type: values.type,
						recurrence: values.recurrence ?? data?.recurrence ?? ListRecurrence.DAILY,
						searchBase: values.searchBase,
						campaigns: selectedCampaign ? [selectedCampaign.id as number] : [],
						onlyNewLeads: values.onlyNewLeads,
						findPersonContactMode: values.findPersonContactMode,
						getContactInformation: values.getContactInformation,
						getEmailInformation: values.getEmailInformation,
						getPhoneInformation: values.getPhoneInformation,
						maxDate: values.maxDate,
						webhooks: values.webhooks,
						priority: values.priority,
						// Job Change tracking fields
						jobChangeIntervalPrio1: values.jobChangeIntervalPrio1,
						jobChangeIntervalPrio1Unit: values.jobChangeIntervalPrio1Unit,
						jobChangeIntervalPrio2: values.jobChangeIntervalPrio2,
						jobChangeIntervalPrio2Unit: values.jobChangeIntervalPrio2Unit,
						jobChangeIntervalPrio3: values.jobChangeIntervalPrio3,
						jobChangeIntervalPrio3Unit: values.jobChangeIntervalPrio3Unit,
						// Job Change deactivation settings
						jobChangeDeactivateOnChangedEmployer: values.jobChangeDeactivateOnChangedEmployer,
						jobChangeDeactivateOnChangedRole: values.jobChangeDeactivateOnChangedRole,
						jobChangeDeactivateOnAddedEmployer: values.jobChangeDeactivateOnAddedEmployer,
						jobChangeDeactivateOnAddedRole: values.jobChangeDeactivateOnAddedRole,
						// Signal interval fields (for TRACKER lists)
						signalIntervalPrio1: values.signalIntervalPrio1,
						signalIntervalPrio1Unit: values.signalIntervalPrio1Unit,
						signalIntervalPrio2: values.signalIntervalPrio2,
						signalIntervalPrio2Unit: values.signalIntervalPrio2Unit,
						signalIntervalPrio3: values.signalIntervalPrio3,
						signalIntervalPrio3Unit: values.signalIntervalPrio3Unit,
						// Signal interval mode configuration (for TRACKER lists)
						signalIntervalMode: values.signalIntervalMode,
						signalScheduleStartDate: values.signalScheduleStartDate,
						// HubSpot deal creation trigger
						...(hasDealCreationTriggerChanged() && selectedDealCreationTrigger
							? { hubspotDealCreationTrigger: selectedDealCreationTrigger }
							: {}),
						// Auto-assign to campaign after lead scoring
						autoAssignToCampaign: values.autoAssignToCampaign,
						autoAssignMinScore: values.autoAssignMinScore,
						...emailDeliverabilityUpdate,
						// Smart List fields
						...(values.smartList ? { smartList: values.smartList } : {}),
					},
				};
			}

			await update({ ...updater }).unwrap();
		} catch (err) {
			console.error(err);
		}
	};

	const onSubmitAndNavigate = async (values: SaveListSettings) => {
		try {
			if (data) {
				await updateCurrentList(data, values);

				// Update HubSpot sync trigger if changed
				await saveSyncTrigger();
				await saveLeadSyncTrigger();

				// Update Zoho sync trigger if changed
				await saveZohoSyncTrigger();

				// Update Pipedrive sync trigger if changed
				await savePipedriveSyncTrigger();
				await saveDynamicsSyncTrigger();
				await saveSalesforceSyncTrigger();

				if (shouldSuggestListActivation(data)) {
					setShowActivateListModal(true);
				} else {
					navigate("/app/lists");
				}
			}
		} catch (err) {
			console.error(err);
		}
	};

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];
	const isSettingsDirty = isDirty || hasEmailDeliverabilityChanged;

	return isFetching || !data ? (
		<LoadingOverlay />
	) : (
		<ListBuilderLayout bodyClassName="w-full p-4 pb-32" sourceTypeLabel={ts(data.sourceType)}>
			<Stepper
				availableSteps={getAvailableSteps(isDetailsNeeded(data), data, isSequencerActive)}
				currentStep="settings"
				doneSteps={getDoneSteps(data)}
				isDirty={isSettingsDirty}
				listId={data.id}
				sourceType={data.sourceType}
			/>
			<div className="w-full">
				<div className="p-4 mt-8 rounded-xl border border-gray-200 bg-brand-white">
					<div className="w-full max-w-[700px]">
						<div className="text-smd mb-5">{tsSettings("title")}</div>
						<div className="flex flex-row gap-4 w-full mb-5">
							<InputField
								error={errors.name?.message}
								handleChange={(event: ChangeEvent<HTMLInputElement>) => {
									setValue("name", event.target.value, {
										shouldDirty: true,
										shouldValidate: true,
									});
								}}
								label={tsSettings("listName")}
								name="name"
								placeholder={ts("name")}
								required={true}
								showError={!!errors.name?.message}
								value={formValues?.name || ""}
							/>
						</div>
						<ListRunScheduleSection
							blockListFrequency={blockListFrequency}
							blockListFrequencyInfo={blockListFrequencyInfo}
							control={control}
							data={data}
							formValues={formValues}
							frequencyOptions={frequencyOptions}
							isSmartList={isSmartList}
							setValue={setValue}
							tsSettings={tsSettings}
						/>

						{data?.sourceType !== ListSourceType.EMPTY &&
							data?.sourceType !== ListSourceType.TRACKER &&
							!isSmartList && (
								<div className="flex flex-col w-full mb-5">
									<div className="flex flex-col gap-2 w-full mb-4">
										<div className="text-ssm font-medium text-text">
											{tsSettings("stopAddingLeadsAfter")}
										</div>
									</div>
									<div className="flex flex-row gap-11 w-full mb-0">
										<div className="text-ssm font-medium text-text">
											{data.sourceType !== ListSourceType.CSV_ENRICHMENTS ? (
												<InputField
													error={!!errors.maxProspects?.message}
													handleChange={(event: ChangeEvent<HTMLInputElement>) => {
														setValue("maxProspects", event.target.value, {
															shouldDirty: true,
															shouldValidate: true,
														});
													}}
													label={tsSettings("numberOfLeads")}
													name="maxProspects"
													placeholder={tsSettings("numberOfLeadsPlaceholder")}
													showError={!!errors.maxProspects?.message}
													type="number"
													value={formValues?.maxProspects || ""}
												/>
											) : (
												<Checkbox
													isChecked={allowMaxProspects}
													name="allowMaxProspects"
													onChange={(e) => {
														const attr = e.target as HTMLInputElement;

														if (
															attr.alt === "CheckIcon" ||
															attr.classList?.toString().includes("CheckIcon")
														) {
															setAllowMaxProspects(!allowMaxProspects);

															if (allowMaxProspects) {
																setValue("maxProspects", "", {
																	shouldDirty: true,
																	shouldValidate: true,
																});
															}
														}
													}}
												>
													<InputField
														error={!!errors.maxProspects?.message}
														handleChange={(event: ChangeEvent<HTMLInputElement>) => {
															setValue("maxProspects", event.target.value, {
																shouldDirty: true,
																shouldValidate: true,
															});
														}}
														isDisabled={!allowMaxProspects}
														label={tsSettings("numberOfLeads")}
														name="maxProspects"
														placeholder={tsSettings("numberOfLeadsPlaceholder")}
														showError={!!errors.maxProspects?.message}
														value={formValues?.maxProspects || ""}
													/>
												</Checkbox>
											)}
										</div>
										{formValues?.type === ListType.ON_GOING && (
											<div className="text-ssm font-medium text-text">
												<Checkbox
													isChecked={allowEndDate}
													name="allowEndDate"
													onChange={(e) => {
														const attr = e.target as HTMLInputElement;

														if (
															attr.alt === "CheckIcon" ||
															attr.classList?.toString().includes("CheckIcon")
														) {
															setAllowEndDate(!allowEndDate);

															if (allowEndDate) {
																setValue("maxDate", null, {
																	shouldDirty: true,
																	shouldValidate: true,
																});
															}
														}
													}}
												>
													<div className="text-ssm font-medium text-text">{tsSettings("date")}</div>
													<LocalizationProvider
														adapterLocale={currentLocale}
														dateAdapter={AdapterDateFns}
														localeText={
															enUSPicker.components.MuiLocalizationProvider.defaultProps.localeText
														}
													>
														<DatePicker
															disabled={!allowEndDate}
															minDate={new Date()}
															name="maxDate"
															slotProps={{
																field: {
																	readOnly: true,
																},
															}}
															sx={{
																width: "100%",
																height: 48,
																marginBottom: 1,
																"& .MuiFilledInput-root": {
																	background: "white",
																	minHeight: 48,
																	marginBottom: 0,
																	borderRadius: 1.5,
																	paddingTop: "10px",
																	paddingBottom: "10px !important",
																	paddingLeft: 1.5,
																	"& .MuiFilledInput-input": {
																		padding: 0,
																	},
																},
															}}
															value={formValues?.maxDate ? new Date(formValues?.maxDate) : null}
															onChange={(value) => {
																if (value !== null) {
																	setValue("maxDate", value.toISOString(), {
																		shouldDirty: true,
																		shouldValidate: true,
																	});
																}
															}}
														/>
													</LocalizationProvider>
												</Checkbox>
											</div>
										)}
									</div>
								</div>
							)}

						<>
							<div className="flex flex-col w-full mb-4">
								<div className="flex flex-col gap-2 w-full mb-4">
									<div className="text-ssm font-medium text-text">
										{tsSettings("findContactInformationTitle")}{" "}
										<Information information={tsSettings("findContactInformationInfo")} />
									</div>
								</div>

								<div className="flex flex-row items-center gap-8 text-ssm text-text">
									<div className="shrink-0">
										<Toggle
											isChecked={allowFindEmail}
											name="allowFindEmail"
											onChange={() => {
												const startState = allowFindEmail;

												setAllowFindEmail(!allowFindEmail);

												if (!startState) {
													setValue(
														"getEmailInformation",
														GetEmailInformation.WHEN_TRIED_TO_SEND_EMAIL,
														{
															shouldDirty: true,
															shouldValidate: true,
														}
													);
												} else {
													setValue("getEmailInformation", GetEmailInformation.NONE, {
														shouldDirty: true,
														shouldValidate: true,
													});
												}
											}}
										/>
									</div>
									<div className="flex-1 min-w-0">
										<Dropdown
											data={[
												{
													title: tsSettings(GetEmailInformation.WHEN_TRIED_TO_SEND_EMAIL),
													id: GetEmailInformation.WHEN_TRIED_TO_SEND_EMAIL,
												},
												{
													title: tsSettings(GetEmailInformation.TRY_TO_FIND_EMAIL),
													id: GetEmailInformation.TRY_TO_FIND_EMAIL,
												},
												{
													title: tsSettings(GetEmailInformation.WHEN_ASSIGNED),
													id: GetEmailInformation.WHEN_ASSIGNED,
												},
											]}
											defaultValue={
												formValues?.getEmailInformation
													? {
															id: formValues.getEmailInformation,
															title: tsSettings(formValues.getEmailInformation),
														}
													: {
															title: tsSettings(GetEmailInformation.WHEN_TRIED_TO_SEND_EMAIL),
															id: GetEmailInformation.WHEN_TRIED_TO_SEND_EMAIL,
														}
											}
											disabled={!allowFindEmail}
											floating={true}
											handleSelect={(value: DropdownAutoCompleteItem) => {
												setValue("getEmailInformation", value.id as GetEmailInformation, {
													shouldDirty: true,
													shouldValidate: true,
												});
											}}
										/>
									</div>
								</div>
							</div>
							<div className="flex flex-col w-full mb-4">
								<div className="flex flex-row items-center gap-8 text-ssm text-text">
									<Toggle
										isChecked={allowFindPhone}
										name="allowFindPhone"
										onChange={() => {
											const startState = allowFindPhone;

											setAllowFindPhone(!allowFindPhone);

											if (!startState) {
												setValue("getPhoneInformation", GetPhoneInformation.TRY_TO_FIND_PHONE, {
													shouldDirty: true,
													shouldValidate: true,
												});
											} else {
												setValue("getPhoneInformation", GetPhoneInformation.NONE, {
													shouldDirty: true,
													shouldValidate: true,
												});
											}
										}}
									/>
									<Dropdown
										data={[
											{
												title: tsSettings(`phone.${GetPhoneInformation.TRY_TO_FIND_PHONE}`),
												id: GetPhoneInformation.TRY_TO_FIND_PHONE,
											},
										]}
										defaultValue={
											formValues?.getPhoneInformation
												? {
														id: formValues.getPhoneInformation,
														title: tsSettings(`phone.${formValues.getPhoneInformation}`),
													}
												: {
														title: tsSettings(GetPhoneInformation.TRY_TO_FIND_PHONE),
														id: GetPhoneInformation.TRY_TO_FIND_PHONE,
													}
										}
										disabled={!allowFindPhone}
										floating={true}
										handleSelect={(value: DropdownAutoCompleteItem) => {
											if (value.id === GetPhoneInformation.TRY_TO_FIND_PHONE) {
												setValue("getPhoneInformation", GetPhoneInformation.TRY_TO_FIND_PHONE, {
													shouldDirty: true,
													shouldValidate: true,
												});
											}
										}}
									/>
								</div>
							</div>
							<div className="flex flex-col w-full mb-4">
								<div className="flex flex-row items-center gap-8 text-ssm text-text">
									<Toggle
										isChecked={allowOnlyContactInformation}
										name="allowOnlyContactInformation"
										onChange={() => {
											const startState = allowOnlyContactInformation;

											setAllowOnlyContactInformation(!allowOnlyContactInformation);

											if (!startState) {
												setValue("getContactInformation", GetContactInformation.ALWAYS, {
													shouldDirty: true,
													shouldValidate: true,
												});
											} else {
												setValue("getContactInformation", GetContactInformation.NONE, {
													shouldDirty: true,
													shouldValidate: true,
												});
											}
										}}
									/>
									<Dropdown
										data={[
											{
												title: tsSettings(GetContactInformation.ALWAYS),
												id: GetContactInformation.ALWAYS,
											},
											{
												title: tsSettings(GetContactInformation.ONLY_WHEN_NO_EMAIL_FOUND),
												id: GetContactInformation.ONLY_WHEN_NO_EMAIL_FOUND,
											},
										]}
										defaultValue={
											formValues?.getContactInformation
												? {
														id: formValues.getContactInformation,
														title: tsSettings(`contact.${formValues.getContactInformation}`),
													}
												: {
														title: tsSettings(GetContactInformation.ALWAYS),
														id: GetContactInformation.NONE,
													}
										}
										disabled={!allowOnlyContactInformation}
										floating={true}
										handleSelect={(value: DropdownAutoCompleteItem) => {
											if (value.id === GetContactInformation.ALWAYS) {
												setValue("getContactInformation", GetContactInformation.ALWAYS, {
													shouldDirty: true,
													shouldValidate: true,
												});
											}

											if (value.id === GetContactInformation.ONLY_WHEN_NO_EMAIL_FOUND) {
												setValue(
													"getContactInformation",
													GetContactInformation.ONLY_WHEN_NO_EMAIL_FOUND,
													{
														shouldDirty: true,
														shouldValidate: true,
													}
												);

												if (!allowFindEmail) {
													setAllowFindEmail(true);
													setValue("getEmailInformation", GetEmailInformation.TRY_TO_FIND_EMAIL, {
														shouldDirty: true,
														shouldValidate: true,
													});
												}
											}
										}}
									/>
								</div>
							</div>
							{!isSmartList && data && (
								<LeadNoveltySetting
									findPersonContactMode={formValues?.findPersonContactMode}
									list={data}
									onFindPersonContactModeChange={(mode) =>
										setValue("findPersonContactMode", mode, { shouldDirty: true })
									}
									onlyNewLeads={formValues?.onlyNewLeads || false}
									onOnlyNewLeadsChange={(value) =>
										setValue("onlyNewLeads", value, { shouldDirty: true, shouldValidate: true })
									}
								/>
							)}
						</>
					</div>
				</div>

				<div className="flex flex-col gap-4 w-full mb-4 mt-4">
					{getAllErrors(undefined, formErrors).length
						? renderErrorMessages(getAllErrors(undefined, formErrors))
						: null}
				</div>
			</div>

			{/* Job Change Settings Section */}
			{data?.sourceType === ListSourceType.JOB_CHANGE && (
				<ListJobChangeSettingsSection
					formValues={formValues}
					setValue={setValue}
					tsSettings={tsSettings}
				/>
			)}

			{/* Signal Scoring Intervals Section (for TRACKER lists) */}
			{data?.sourceType === ListSourceType.TRACKER && (
				<div
					className={classNames(
						"border p-6 mb-8 w-full items-start rounded-md border-1 border-border-light",
						"flex flex-col text-left gap-6",
						signalSettings ? "bg-brand-white" : "bg-white"
					)}
				>
					<button
						className={classNames(
							"w-full items-center group cursor-pointer flex flex-row text-left gap-6"
						)}
						onClick={() => {
							setSignalSettings(!signalSettings);
						}}
					>
						<div className="grow">
							<div className="text-smd text-text font-bold">{tsSettings("signalSettings")}</div>
							<div className="text-ssm text-text-meta">
								{tsSettings("signalSettingsDescription")}
							</div>
						</div>
						<div
							className={classNames(
								"flex !w-[36px] !h-[36px] group-hover:bg-blue-action rounded-md items-center justify-center border",
								signalSettings
									? "border-border-light bg-white"
									: "border-transparent bg-background-light"
							)}
						>
							<Icon
								className="text-text-meta group-hover:text-white w-[12px] h-auto"
								icon={signalSettings ? faChevronUp : faChevronDown}
							/>
						</div>
					</button>

					{signalSettings && (
						<div className="flex flex-col w-full">
							{/* Signal Interval Mode Selection */}
							<div className="flex flex-col gap-2 w-full mb-6">
								<div className="text-ssm font-medium text-text">
									{tsSettings("signalIntervalMode")}
								</div>
								<div className="text-ssm text-text-meta mb-2">
									{tsSettings("signalIntervalModeDescription")}
								</div>

								{/* Radio buttons for mode selection */}
								<div className="flex flex-col gap-3">
									<label className="flex items-start gap-3 cursor-pointer">
										<input
											checked={
												formValues?.signalIntervalMode === SignalIntervalMode.ROLLING ||
												!formValues?.signalIntervalMode
											}
											className="mt-1 w-4 h-4 text-blue-600 cursor-pointer"
											name="signalIntervalMode"
											type="radio"
											value={SignalIntervalMode.ROLLING}
											onChange={() => {
												setValue("signalIntervalMode", SignalIntervalMode.ROLLING, {
													shouldDirty: true,
													shouldValidate: true,
												});
												// Clear start date when switching to rolling mode
												setValue("signalScheduleStartDate", null, {
													shouldDirty: true,
													shouldValidate: true,
												});
											}}
										/>
										<div className="flex flex-col">
											<span className="text-sm font-medium text-text">
												{tsSettings("signalModeRolling")}
											</span>
											<span className="text-ssm text-text-meta">
												{tsSettings("signalModeRollingDescription")}
											</span>
										</div>
									</label>

									<label className="flex items-start gap-3 cursor-pointer">
										<input
											checked={formValues?.signalIntervalMode === SignalIntervalMode.FIXED}
											className="mt-1 w-4 h-4 text-blue-600 cursor-pointer"
											name="signalIntervalMode"
											type="radio"
											value={SignalIntervalMode.FIXED}
											onChange={() => {
												setValue("signalIntervalMode", SignalIntervalMode.FIXED, {
													shouldDirty: true,
													shouldValidate: true,
												});
												// Set default start date to today when switching to fixed mode
												if (!formValues?.signalScheduleStartDate) {
													setValue(
														"signalScheduleStartDate",
														new Date().toISOString().split("T")[0],
														{
															shouldDirty: true,
															shouldValidate: true,
														}
													);
												}
											}}
										/>
										<div className="flex flex-col">
											<span className="text-sm font-medium text-text">
												{tsSettings("signalModeFixed")}
											</span>
											<span className="text-ssm text-text-meta">
												{tsSettings("signalModeFixedDescription")}
											</span>
										</div>
									</label>
								</div>

								{/* Date Picker for Fixed Mode */}
								{formValues?.signalIntervalMode === SignalIntervalMode.FIXED && (
									<div className="mt-4 w-fit min-w-[260px]">
										<div className="text-ssm font-medium text-text mb-2">
											{tsSettings("signalScheduleStartDate")}
										</div>
										<Datepicker
											asSingle={true}
											containerClassName="border border-border-light rounded-xl p-0 text-sm text-text-meta relative"
											inputClassName="h-[46px] relative transition-all duration-300 py-2.5 pl-4 pr-14 w-full border-gray-300 dark:bg-slate-800 dark:text-white/80 dark:border-slate-600 rounded-xl tracking-wide font-light text-sm placeholder-gray-400 bg-white focus:ring disabled:opacity-40 disabled:cursor-not-allowed focus:border-blue-500 focus:ring-blue-500/20"
											placeholder={tsSettings("signalScheduleStartDate")}
											showFooter={true}
											showShortcuts={true}
											useRange={false}
											value={
												formValues?.signalScheduleStartDate
													? {
															startDate: new Date(formValues.signalScheduleStartDate),
															endDate: new Date(formValues.signalScheduleStartDate),
														}
													: {
															startDate: new Date(),
															endDate: new Date(),
														}
											}
											onChange={(value: DateValueType) => {
												if (value?.startDate) {
													const dateValue =
														typeof value.startDate === "string"
															? value.startDate
															: value.startDate.toISOString().split("T")[0];

													setValue("signalScheduleStartDate", dateValue, {
														shouldDirty: true,
														shouldValidate: true,
													});
												}
											}}
										/>
									</div>
								)}
							</div>

							{/* Tracking Frequency Section */}
							<div className="flex flex-col gap-2 w-full mb-4">
								<div className="text-ssm font-medium text-text">
									{tsSettings("signalTrackingFrequency")}
								</div>
							</div>

							{/* Frequency Table */}
							<div className="border border-gray-200 rounded-lg mb-4">
								{/* Prio 1 Row */}
								<div className="flex flex-row items-center border-b border-gray-200 p-4">
									<div className="w-48 font-medium text-sm text-text">
										{tsSettings("signalCheckPrio1")}
									</div>
									<div className="flex flex-row items-center gap-2">
										<span className="text-sm text-text-meta">{tsSettings("signalEvery")}</span>
										<div className="w-20">
											<Dropdown
												data={intervalNumberOptions}
												floating={true}
												handleSelect={(value: DropdownAutoCompleteItem) => {
													if (value.id !== formValues?.signalIntervalPrio1) {
														setValue("signalIntervalPrio1", value.id as number, {
															shouldDirty: true,
															shouldValidate: true,
														});
													}
												}}
												defaultValue={findNumberOption(
													formValues?.signalIntervalPrio1,
													DEFAULT_SIGNAL_INTERVALS.prio1
												)}
											/>
										</div>
										<div className="w-28">
											<Dropdown
												data={intervalUnitOptions}
												floating={true}
												handleSelect={(value: DropdownAutoCompleteItem) => {
													if (value.id !== formValues?.signalIntervalPrio1Unit) {
														setValue("signalIntervalPrio1Unit", value.id as JobChangeIntervalUnit, {
															shouldDirty: true,
															shouldValidate: true,
														});
													}
												}}
												defaultValue={findUnitOption(
													formValues?.signalIntervalPrio1Unit,
													DEFAULT_SIGNAL_INTERVALS.prio1Unit
												)}
											/>
										</div>
										<span className="text-sm text-text-meta ml-4">
											{tsSettings("signalCreditsPerRun")}
										</span>
									</div>
								</div>

								{/* Prio 2 Row */}
								<div className="flex flex-row items-center border-b border-gray-200 p-4">
									<div className="w-48 font-medium text-sm text-text">
										{tsSettings("signalCheckPrio2")}
									</div>
									<div className="flex flex-row items-center gap-2">
										<span className="text-sm text-text-meta">{tsSettings("signalEvery")}</span>
										<div className="w-20">
											<Dropdown
												data={intervalNumberOptions}
												floating={true}
												handleSelect={(value: DropdownAutoCompleteItem) => {
													if (value.id !== formValues?.signalIntervalPrio2) {
														setValue("signalIntervalPrio2", value.id as number, {
															shouldDirty: true,
															shouldValidate: true,
														});
													}
												}}
												defaultValue={findNumberOption(
													formValues?.signalIntervalPrio2,
													DEFAULT_SIGNAL_INTERVALS.prio2
												)}
											/>
										</div>
										<div className="w-28">
											<Dropdown
												data={intervalUnitOptions}
												floating={true}
												handleSelect={(value: DropdownAutoCompleteItem) => {
													if (value.id !== formValues?.signalIntervalPrio2Unit) {
														setValue("signalIntervalPrio2Unit", value.id as JobChangeIntervalUnit, {
															shouldDirty: true,
															shouldValidate: true,
														});
													}
												}}
												defaultValue={findUnitOption(
													formValues?.signalIntervalPrio2Unit,
													DEFAULT_SIGNAL_INTERVALS.prio2Unit
												)}
											/>
										</div>
										<span className="text-sm text-text-meta ml-4">
											{tsSettings("signalCreditsPerRun")}
										</span>
									</div>
								</div>

								{/* Prio 3 Row */}
								<div className="flex flex-row items-center p-4">
									<div className="w-48 font-medium text-sm text-text">
										{tsSettings("signalCheckPrio3")}
									</div>
									<div className="flex flex-row items-center gap-2">
										<span className="text-sm text-text-meta">{tsSettings("signalEvery")}</span>
										<div className="w-20">
											<Dropdown
												data={intervalNumberOptions}
												floating={true}
												handleSelect={(value: DropdownAutoCompleteItem) => {
													if (value.id !== formValues?.signalIntervalPrio3) {
														setValue("signalIntervalPrio3", value.id as number, {
															shouldDirty: true,
															shouldValidate: true,
														});
													}
												}}
												defaultValue={findNumberOption(
													formValues?.signalIntervalPrio3,
													DEFAULT_SIGNAL_INTERVALS.prio3
												)}
											/>
										</div>
										<div className="w-28">
											<Dropdown
												data={intervalUnitOptions}
												floating={true}
												handleSelect={(value: DropdownAutoCompleteItem) => {
													if (value.id !== formValues?.signalIntervalPrio3Unit) {
														setValue("signalIntervalPrio3Unit", value.id as JobChangeIntervalUnit, {
															shouldDirty: true,
															shouldValidate: true,
														});
													}
												}}
												defaultValue={findUnitOption(
													formValues?.signalIntervalPrio3Unit,
													DEFAULT_SIGNAL_INTERVALS.prio3Unit
												)}
											/>
										</div>
										<span className="text-sm text-text-meta ml-4">
											{tsSettings("signalCreditsPerRun")}
										</span>
									</div>
								</div>
							</div>
						</div>
					)}
				</div>
			)}

			<ListEditSettingCrmSections
				dynamics={{
					isEnabled: isDynamicsEnabled,
					isConnected: isDynamicsConnected,
					listSyncTrigger: dynamicsListSyncTrigger,
					selected: selectedDynamicsSyncTrigger,
					setSelected: setSelectedDynamicsSyncTrigger,
				}}
				hubspot={{
					isEnabled: isHubspotEnabled,
					isConnected: isHubspotConnected,
					listSyncTrigger,
					selectedSyncTrigger: selectedHubspotSyncTrigger,
					setSelectedSyncTrigger: setSelectedHubspotSyncTrigger,
					listLeadSyncTrigger,
					selectedLeadSyncTrigger,
					setSelectedLeadSyncTrigger,
					dealCreationEffectiveTrigger,
					selectedDealCreationTrigger,
					setSelectedDealCreationTrigger,
				}}
				listId={+id}
				pipedrive={{
					isEnabled: isPipedriveEnabled,
					isConnected: isPipedriveConnected,
					listSyncTrigger: pipedriveListSyncTrigger,
					selected: selectedPipedriveSyncTrigger,
					setSelected: setSelectedPipedriveSyncTrigger,
				}}
				salesforce={{
					isEnabled: isSalesforceEnabled,
					isConnected: isSalesforceConnected,
					listSyncTrigger: salesforceListSyncTrigger,
					selected: selectedSalesforceSyncTrigger,
					setSelected: setSelectedSalesforceSyncTrigger,
				}}
				zoho={{
					isEnabled: isZohoEnabled,
					isConnected: isZohoConnected,
					listSyncTrigger: zohoListSyncTrigger,
					selected: selectedZohoSyncTrigger,
					setSelected: setSelectedZohoSyncTrigger,
				}}
			/>
			<ListEditSettingEmailDeliverability
				listId={data.id}
				overrides={overrides}
				sourceType={data.sourceType}
			/>
			<div
				className={classNames(
					"border p-6 mb-8 w-full items-start rounded-md border-1 border-border-light",
					"flex flex-col text-left gap-6",
					advancedSettings ? "bg-brand-white" : "bg-white"
				)}
			>
				<button
					className={classNames(
						"w-full items-center group cursor-pointer flex flex-row text-left gap-6"
					)}
					onClick={() => {
						setAdvancedSettings(!advancedSettings);
					}}
				>
					<div className="grow">
						<div className="text-smd text-text font-bold">{tsSettings("advancedSettings")}</div>
						<div className="text-ssm text-text-meta">
							{tsSettings("advancedSettingsDescription")}
						</div>
					</div>
					<div
						className={classNames(
							"flex !w-[36px] !h-[36px] group-hover:bg-blue-action rounded-md items-center justify-center border",
							advancedSettings
								? "border-border-light bg-white"
								: "border-transparent bg-background-light"
						)}
					>
						<Icon
							className="text-text-meta group-hover:text-white w-[12px] h-auto"
							icon={advancedSettings ? faChevronUp : faChevronDown}
						/>
					</div>
				</button>

				{advancedSettings && (
					<div className="flex flex-col w-full">
						<div className="flex flex-col w-full mb-8">
							<AutoComplete
								data={
									lemlists?.data?.map((c) => {
										return { id: c.id, title: c.name } as AutoCompleteItem;
									}) || []
								}
								handleSearch={(value: string) => setSearchLemlist(value)}
								handleSelect={(value?: AutoCompleteItem) => {
									setSelectedCampaign(value);

									setValue("campaigns", [value?.id as number], {
										shouldDirty: true,
										shouldValidate: true,
									});
								}}
								icon={faMegaphone}
								isLoading={isLoadingLemlists}
								isLoadingText={tsSettings("loadingCampaigns")}
								label={tsSettings("selectDefaultCampaign")}
								placeholder={tsSettings("selectCampaignPlaceholder")}
								selectedItem={selectedCampaign?.title ?? ""}
							/>
						</div>
						<div className="flex flex-col w-full mb-8">
							<Checkbox
								icon="information"
								information={tsSettings("autoAssignToCampaignInfo")}
								isChecked={formValues?.autoAssignToCampaign || false}
								label={tsSettings("autoAssignToCampaign")}
								name="autoAssignToCampaign"
								onChange={() => {
									const newValue = !formValues?.autoAssignToCampaign;

									setValue("autoAssignToCampaign", newValue, {
										shouldDirty: true,
										shouldValidate: true,
									});

									if (!newValue) {
										setValue("autoAssignMinScore", null, {
											shouldDirty: true,
											shouldValidate: true,
										});
									}
								}}
							>
								{tsSettings("autoAssignToCampaign")}
							</Checkbox>
							{formValues?.autoAssignToCampaign && (
								<div className="mt-4 ml-6">
									<Dropdown
										data={[
											{
												id: "1",
												title: tsSettings("autoAssignScore1"),
											},
											{
												id: "2",
												title: tsSettings("autoAssignScore2"),
											},
											{
												id: "3",
												title: tsSettings("autoAssignScore3"),
											},
											{
												id: "Disqualified",
												title: tsSettings("autoAssignScoreDisqualified"),
											},
										]}
										defaultValue={
											formValues?.autoAssignMinScore
												? {
														id: formValues.autoAssignMinScore,
														title:
															formValues.autoAssignMinScore === "1"
																? tsSettings("autoAssignScore1")
																: formValues.autoAssignMinScore === "2"
																	? tsSettings("autoAssignScore2")
																	: formValues.autoAssignMinScore === "3"
																		? tsSettings("autoAssignScore3")
																		: tsSettings("autoAssignScoreDisqualified"),
													}
												: undefined
										}
										error={!!errors.autoAssignMinScore}
										handleSelect={(value: DropdownAutoCompleteItem) => {
											setValue("autoAssignMinScore", value.id as string, {
												shouldDirty: true,
												shouldValidate: true,
											});
										}}
										label={tsSettings("autoAssignMinScore")}
									/>
									{data && (
										<AutoAssignRoutingLine
											autoAssignEnabled={!!data.autoAssignToCampaign}
											campaignConfig={data.campaignConfig}
											campaignStatuses={data.campaignStatuses}
											listId={data.id}
										/>
									)}
								</div>
							)}
						</div>
						<div className="flex flex-col w-full mb-4">
							<div className="flex flex-col gap-2 w-full mb-4">
								<div className="text-ssm font-medium text-text">
									{tsSettings("sendLeadsToWebhook")}{" "}
								</div>
							</div>
							{webhooks?.data?.map((webhook) => {
								return (
									<div
										key={webhook.id}
										className="flex flex-row items-center gap-8 text-ssm text-text mb-4 w-full"
									>
										<Toggle
											isChecked={
												formValues?.webhooks?.some((w) => w.webhookId === webhook.id) || false
											}
											name={`webhook-${webhook.id}`}
											onChange={() => {
												if (formValues?.webhooks?.some((w) => w.webhookId === webhook.id)) {
													setValue(
														"webhooks",
														formValues?.webhooks?.filter((w) => w.webhookId !== webhook.id),
														{
															shouldDirty: true,
															shouldValidate: true,
														}
													);
												} else {
													setValue(
														"webhooks",
														[
															...(formValues?.webhooks || []),
															{
																listId: data?.id,
																webhookId: webhook.id,
																type: WebhookType.WEBHOOK_ALL_QUALIFIED_LEADS,
															},
														],
														{
															shouldDirty: true,
															shouldValidate: true,
														}
													);
												}
											}}
										/>
										<Tooltip content={webhook.name}>
											<div className="text-ssm text-text whitespace-nowrap w-[250px] min-w-[250px] overflow-hidden text-ellipsis">
												{webhook.name}
											</div>
										</Tooltip>
										<div className="min-w-[400px]">
											<Dropdown
												data={[
													{
														title: tsSettings(WebhookType.WEBHOOK_ALL_QUALIFIED_LEADS),
														id: WebhookType.WEBHOOK_ALL_QUALIFIED_LEADS,
													},
													{
														title: tsSettings(WebhookType.WEBHOOK_ALL_LEADS),
														id: WebhookType.WEBHOOK_ALL_LEADS,
													},
													{
														title: tsSettings(WebhookType.WEBHOOK_ALL_LEADS_INCLUDING_NOT_FOUND),
														id: WebhookType.WEBHOOK_ALL_LEADS_INCLUDING_NOT_FOUND,
													},
													{
														title: tsSettings(WebhookType.WEBHOOK_WHEN_ASSIGNED),
														id: WebhookType.WEBHOOK_WHEN_ASSIGNED,
													},
													{
														title: tsSettings(WebhookType.WEBHOOK_WHEN_REPLIED),
														id: WebhookType.WEBHOOK_WHEN_REPLIED,
													},
												]}
												defaultValue={
													formValues?.webhooks?.find((w) => w.webhookId === webhook.id)
														? {
																id: formValues?.webhooks?.find((w) => w.webhookId === webhook.id)
																	?.type as string,
																title: tsSettings(
																	formValues?.webhooks?.find((w) => w.webhookId === webhook.id)
																		?.type as string
																),
															}
														: {
																title: tsSettings(WebhookType.WEBHOOK_ALL_QUALIFIED_LEADS),
																id: WebhookType.WEBHOOK_ALL_QUALIFIED_LEADS,
															}
												}
												disabled={!formValues?.webhooks?.some((w) => w.webhookId === webhook.id)}
												floating={true}
												handleSelect={(value: DropdownAutoCompleteItem) => {
													const currentWebhook = formValues?.webhooks?.find(
														(w) => w.webhookId === webhook.id
													);

													if (currentWebhook?.webhookId) {
														const allOtherWebhooks = formValues?.webhooks?.filter(
															(w) => w.webhookId !== currentWebhook?.webhookId
														);

														setValue(
															"webhooks",
															[
																...(allOtherWebhooks ?? []),
																{
																	...currentWebhook,
																	type: value.id as WebhookType,
																},
															],
															{
																shouldDirty: true,
																shouldValidate: true,
															}
														);
													}
												}}
											/>
										</div>
									</div>
								);
							})}
						</div>
					</div>
				)}
			</div>

			<div className="absolute mt-14 h-16 flex flex-row justify-between items-center w-auto border-t border-t-border px-8 py-[14px] rounded-t-16 bottom-0 right-0 z-[999] bg-white w-full">
				<div></div>
				<div className="flex flex-row self-left items-center gap-x-4">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						title={t("basics.previous")}
						onClick={() => {
							if (isSmartList) {
								navigate(
									isSequencerActive
										? `/app/lists/create/copywriting/${id}`
										: `/app/lists/create/source/${id}`
								);
							} else if (isSequencerActive) {
								navigate(`/app/lists/create/copywriting/${id}`);
							} else if (isDetailsNeeded(data)) {
								navigate(`/app/lists/create/details/${id}`);
							} else {
								navigate(`/app/lists/create/filter/${id}`);
							}
						}}
					/>
					<Button
						color={ButtonColor.BRAND}
						disabled={!isSettingsDirty}
						title={t("basics.save")}
						onClick={handleSubmit(onSubmit)}
					/>
					<Button
						isLoading={isLoading}
						title={t("basics.next")}
						onClick={handleSubmit(onSubmitAndNavigate)}
					/>
				</div>
			</div>
			<ActivateListModal
				activateCampaign={activateCampaign}
				isOpen={showActivateListModal}
				onActivate={() => void handleActivateList(data.id, activateCampaign)}
				onClose={() => setShowActivateListModal(false)}
				onGoToLists={() => navigate("/app/lists")}
				onToggleActivateCampaign={() => setActivateCampaign(!activateCampaign)}
			/>
			{renderMessageSnackbar()}
		</ListBuilderLayout>
	);
};
